import { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import CinematicNavbar from "@/components/home/CinematicNavbar";
import Footer from "@/components/Footer";
import PageBreadcrumb from "@/components/PageBreadcrumb";
import UniversalSEO from "@/components/SEO/UniversalSEO";
import ScrollToTopButton from "@/components/ScrollToTopButton";
import ScrollToTop from "@/components/ScrollToTop";
import ScrollProgress from "@/components/ScrollProgress";
import GoogleAnalytics from "@/components/SEO/GoogleAnalytics";
import StickyCtaBar from "@/components/conversion/StickyCtaBar";

import ExitIntentModal from "@/components/conversion/ExitIntentModal";

interface StandardLayoutProps {
  children: ReactNode;
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  withPadding?: boolean;
  showBreadcrumb?: boolean;
  noindex?: boolean;
  breadcrumbLabels?: Record<string, string>;
  className?: string;
  structuredData?: Record<string, unknown>;
  seo?: {
    title?: string;
    description?: string;
    canonical?: string;
    keywords?: string;
    ogImage?: string;
    ogType?: "website" | "article";
    noindex?: boolean;
    indexable?: boolean;
  };
}

/**
 * StandardLayout - Base layout for all standard pages
 * 
 * This layout includes the main navigation, footer, breadcrumbs, and SEO components
 * to ensure consistent structure and metadata across the site.
 */
const StandardLayout = ({ 
  children, 
  title,
  description,
  keywords,
  ogImage = "https://puntohispano.ch/lovable-uploads/team-image.jpg",
  withPadding = true, 
  showBreadcrumb = true,
  noindex = false,
  breadcrumbLabels = {},
  className = "",
  structuredData,
  seo
}: StandardLayoutProps) => {
  const location = useLocation();
  const isHomePage = location.pathname === "/";
  
  // Merge legacy props with new seo object
  const finalSEO = seo || {
    title,
    description,
    keywords,
    ogImage,
    noindex
  };
  
  return (
    <div className="flex flex-col min-h-screen bg-white dark:bg-cinema-dark text-gray-900 dark:text-white transition-colors duration-300">
      {/* Universal SEO Management */}
      <UniversalSEO seo={finalSEO} structuredData={structuredData} />
      
      {/* Google Analytics */}
      <GoogleAnalytics />
      
      <ScrollToTop />
      <ScrollProgress />
      
      <CinematicNavbar />
      
      {/* Breadcrumbs */}
      {showBreadcrumb && !isHomePage && (
        <div className="bg-muted/30 dark:bg-white/5 border-b border-border/50 pt-16 md:pt-20">
          <PageBreadcrumb customLabels={breadcrumbLabels} />
        </div>
      )}
      
      <main className={`flex-grow ${withPadding ? (showBreadcrumb && !isHomePage ? 'pb-16' : 'pt-16 md:pt-20 pb-16') : ''} ${className}`} id="main-content">
        {children}
      </main>
      
      <Footer />
      <ScrollToTopButton />
      
      {/* Conversion Elements */}
      <StickyCtaBar />
      
      <ExitIntentModal />
    </div>
  );
};

export default StandardLayout;
